// src/components/admin/page-management/BackgroundManager.js
import React, { useState } from "react";
import ImageUploader from "./ImageUploader";
import VideoUploader from "./VideoUploader";

const BackgroundManager = ({ pageData, onUpdate, pageSlug }) => {
  const [activeTab, setActiveTab] = useState("color");

  const handleColorChange = (e) => {
    onUpdate({
      ...pageData,
      backgroundType: "color",
      backgroundColor: e.target.value,
    });
  };

  const handleImageUpload = (imageUrl) => {
    onUpdate({
      ...pageData,
      backgroundType: "image",
      backgroundImage: imageUrl,
    });
  };

  const handleVideoUpload = (videoUrl) => {
    onUpdate({
      ...pageData,
      backgroundType: "video",
      backgroundVideo: videoUrl,
    });
  };

  const handleOverlayChange = (overlayConfig) => {
    onUpdate({
      ...pageData,
      backgroundOverlay: overlayConfig,
    });
  };

  return (
    <div className="border rounded p-4 bg-white">
      <h3 className="text-lg font-medium mb-3">Page Background</h3>

      <div className="border-b mb-4">
        <div className="flex">
          <button
            className={`py-2 px-4 border-b-2 ${
              activeTab === "color"
                ? "border-blue-500 text-blue-600"
                : "border-transparent"
            }`}
            onClick={() => setActiveTab("color")}
          >
            Color
          </button>
          <button
            className={`py-2 px-4 border-b-2 ${
              activeTab === "image"
                ? "border-blue-500 text-blue-600"
                : "border-transparent"
            }`}
            onClick={() => setActiveTab("image")}
          >
            Image
          </button>
          <button
            className={`py-2 px-4 border-b-2 ${
              activeTab === "video"
                ? "border-blue-500 text-blue-600"
                : "border-transparent"
            }`}
            onClick={() => setActiveTab("video")}
          >
            Video
          </button>
          <button
            className={`py-2 px-4 border-b-2 ${
              activeTab === "overlay"
                ? "border-blue-500 text-blue-600"
                : "border-transparent"
            }`}
            onClick={() => setActiveTab("overlay")}
          >
            Overlay
          </button>
        </div>
      </div>

      {activeTab === "color" && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Background Color
          </label>
          <div className="flex items-center">
            <input
              type="color"
              value={pageData.backgroundColor || "#ffffff"}
              onChange={handleColorChange}
              className="w-10 h-10 p-1 border rounded mr-2"
            />
            <input
              type="text"
              value={pageData.backgroundColor || "#ffffff"}
              onChange={handleColorChange}
              className="flex-1 p-2 border rounded"
              placeholder="#ffffff or rgba(255,255,255,1)"
            />
          </div>
        </div>
      )}

      {activeTab === "image" && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Background Image
          </label>
          {pageData.backgroundImage && (
            <div className="mb-3">
              <img
                src={pageData.backgroundImage}
                alt="Background Preview"
                className="max-h-40 rounded border"
              />
            </div>
          )}
          <ImageUploader
            onImageUpload={handleImageUpload}
            pageSlug={pageSlug}
          />

          {pageData.backgroundImage && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Background Size
                </label>
                <select
                  className="w-full p-2 border rounded"
                  value={pageData.backgroundSize || "cover"}
                  onChange={(e) =>
                    onUpdate({ ...pageData, backgroundSize: e.target.value })
                  }
                >
                  <option value="cover">Cover</option>
                  <option value="contain">Contain</option>
                  <option value="auto">Auto</option>
                  <option value="100%">100%</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Background Position
                </label>
                <select
                  className="w-full p-2 border rounded"
                  value={pageData.backgroundPosition || "center"}
                  onChange={(e) =>
                    onUpdate({
                      ...pageData,
                      backgroundPosition: e.target.value,
                    })
                  }
                >
                  <option value="center">Center</option>
                  <option value="top">Top</option>
                  <option value="right">Right</option>
                  <option value="bottom">Bottom</option>
                  <option value="left">Left</option>
                  <option value="top left">Top Left</option>
                  <option value="top right">Top Right</option>
                  <option value="bottom left">Bottom Left</option>
                  <option value="bottom right">Bottom Right</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Background Repeat
                </label>
                <select
                  className="w-full p-2 border rounded"
                  value={pageData.backgroundRepeat || "no-repeat"}
                  onChange={(e) =>
                    onUpdate({ ...pageData, backgroundRepeat: e.target.value })
                  }
                >
                  <option value="no-repeat">No Repeat</option>
                  <option value="repeat">Repeat</option>
                  <option value="repeat-x">Repeat-X</option>
                  <option value="repeat-y">Repeat-Y</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Background Attachment
                </label>
                <select
                  className="w-full p-2 border rounded"
                  value={pageData.backgroundAttachment || "scroll"}
                  onChange={(e) =>
                    onUpdate({
                      ...pageData,
                      backgroundAttachment: e.target.value,
                    })
                  }
                >
                  <option value="scroll">Scroll</option>
                  <option value="fixed">Fixed</option>
                  <option value="local">Local</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "video" && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Background Video
          </label>
          {pageData.backgroundVideo && (
            <div className="mb-3">
              <video
                src={pageData.backgroundVideo}
                className="max-h-40 w-full rounded border"
                controls
              />
            </div>
          )}
          <VideoUploader
            onVideoUpload={handleVideoUpload}
            pageSlug={pageSlug}
          />

          {pageData.backgroundVideo && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={pageData.videoLoop || false}
                    onChange={(e) =>
                      onUpdate({ ...pageData, videoLoop: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Loop Video</span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={pageData.videoMuted || true}
                    onChange={(e) =>
                      onUpdate({ ...pageData, videoMuted: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Muted</span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={pageData.videoAutoplay || true}
                    onChange={(e) =>
                      onUpdate({ ...pageData, videoAutoplay: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Autoplay</span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "overlay" && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Background Overlay
          </label>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={pageData.backgroundOverlay?.enabled || false}
                onChange={(e) =>
                  handleOverlayChange({
                    ...(pageData.backgroundOverlay || {}),
                    enabled: e.target.checked,
                  })
                }
                className="mr-2"
              />
              <span className="text-sm">Enable Overlay</span>
            </label>
          </div>

          {pageData.backgroundOverlay?.enabled && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Overlay Color
                </label>
                <div className="flex items-center">
                  <input
                    type="color"
                    value={pageData.backgroundOverlay?.color || "#000000"}
                    onChange={(e) =>
                      handleOverlayChange({
                        ...(pageData.backgroundOverlay || {}),
                        color: e.target.value,
                      })
                    }
                    className="w-10 h-10 p-1 border rounded mr-2"
                  />
                  <input
                    type="text"
                    value={pageData.backgroundOverlay?.color || "#000000"}
                    onChange={(e) =>
                      handleOverlayChange({
                        ...(pageData.backgroundOverlay || {}),
                        color: e.target.value,
                      })
                    }
                    className="flex-1 p-2 border rounded"
                    placeholder="#000000 or rgba(0,0,0,0.5)"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Overlay Opacity (
                  {Math.round(
                    (pageData.backgroundOverlay?.opacity || 0.5) * 100
                  )}
                  %)
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={pageData.backgroundOverlay?.opacity || 0.5}
                  onChange={(e) =>
                    handleOverlayChange({
                      ...(pageData.backgroundOverlay || {}),
                      opacity: parseFloat(e.target.value),
                    })
                  }
                  className="w-full"
                />
              </div>

              <div className="h-20 rounded overflow-hidden relative">
                <div className="absolute inset-0 bg-gray-200 bg-checkerboard"></div>
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundColor:
                      pageData.backgroundOverlay?.color || "#000000",
                    opacity: pageData.backgroundOverlay?.opacity || 0.5,
                  }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center text-white">
                  Overlay Preview
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BackgroundManager;
